import { Component, HostListener, Input, OnDestroy, forwardRef } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validator } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

const FORM_INPUT_CONTROL_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => TimeComponent),
    multi: true
};

const FORM_INPUT_VALIDATOR = {
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => TimeComponent),
    multi: true
};

/**
 * Eine Time Input Komponente.
 * Stellt zwei Input Elementen, die Stunden und Minuten darstellen.
 *
 * Input/Output ist ein String - "h:m:s" (Stunden, Minuten, Sekunden).
 *
 */
@Component({
    selector: 'common-time-input',
    template: `
        <div [formGroup]="form">
            <span class="commonTimepickerTimeContainer">
                <input class="inputField commonTimepickerTime" formControlName="hour" type="number"
                       min="0" max="23"
                       [placeholder]="hourPlaceholder"/>
                <input class="inputField commonTimepickerTime" formControlName="minute" type="number"
                       min="0" max="59"
                       [placeholder]="minutePlaceholder"/>
            </span>
        </div>
    `,
    providers: [FORM_INPUT_CONTROL_VALUE_ACCESSOR, FORM_INPUT_VALIDATOR]
})
export class TimeComponent implements ControlValueAccessor, Validator, OnDestroy {

    /** Input Placeholder. */
    @Input() hourPlaceholder = '00';

    @Input() minutePlaceholder = '00';

    form: UntypedFormGroup;


    private blur$ = new Subject<any>();

    private value$: Observable<TimeValueString>;

    constructor(private fb: UntypedFormBuilder) {
        this.form = fb.group({
            hour: [null],
            minute: [null],
            second: [null] // momentan zeigen wir die Sekunden nicht, es wird aber besser die zu berücksichtigen.
        });

        this.value$ = this.form.valueChanges.pipe(
            map(parseValue),
            valueToStringOperator,
            distinctUntilChanged() // https://github.com/angular/angular/issues/12540
        );
    }

    @HostListener('blur')
    onBlur(e: any): void {
        this.blur$.next(e);
    }

    ngOnDestroy(): void {
        this.blur$.complete();
    }

    registerOnChange(fn: (v: string) => void): void {
        // Braucht kein "unsubscribe", da form.valueChanges ein Subject ist, und
        // das wird automatisch mit der Komponente zerstört.
        this.value$.subscribe(v => fn(v));
    }

    registerOnTouched(fn: any): void {
        this.blur$.subscribe(e => fn(e));
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            return this.form.disable({ emitEvent: false });
        }
        this.form.enable({ emitEvent: false });
    }

    writeValue(time: TimeValueString): void {
        if (time === null) {
            this.form.reset(undefined, { emitEvent: false });
            return;
        }
        this.form.patchValue(this.stringToValue(time));
    }

    validate(control: AbstractControl): ValidationErrors | null {
        if (this.form.invalid) {
            return {
                hour: this.form.get('hour').errors,
                minute: this.form.get('minute').errors
            };
        }
        return null;
    }

    private stringToValue(valueString: TimeValueString): TimeValueObject {
        const valueArray = valueString.split(':');
        return {
            hour: parseInt(valueArray[0]),
            minute: parseInt(valueArray[1]),
            second: parseInt(valueArray[2])
        };
    }
}

type TimeValueObject = { hour: number, minute: number, second: number };
type FormTimeValueObject = { hour: number | null, minute: number | null, second: number | null };
type TimeValueString = string;

function valueToStringOperator(source$: Observable<TimeValueObject | null>): Observable<TimeValueString> {
    return source$.pipe(
        map(v => {
            if (v === null) {
                return null;
            }
            return [v.hour, v.minute, v.second].join(':');
        })
    );
}

function parseValue(value: FormTimeValueObject | null | undefined): TimeValueObject | null {
    if (value === null || value === undefined || (typeof value) !== 'object') {
        return null;
    }
    if ((value.hour === null || isNaN(value.hour)) && (value.minute === null || isNaN(value.minute))) {
        return null;
    }

    return {
        hour: value.hour || 0,
        minute: value.minute || 0,
        second: value.second || 0
    }
}
